import { useTranslation } from "react-i18next";

interface UrlInputPanelProps {
  url: string;
  attestation: boolean;
  isUploading: boolean;
  onUrlChange: (url: string) => void;
  onAttestationChange: (checked: boolean) => void;
}

/**
 * URL-scan input panel (pirx-27tn.7): a single public-page URL field plus the
 * mandatory Tier-A attestation and the single-page/point-in-time-snapshot
 * caveat. The submit gate (URL present + attestation checked) lives in the
 * parent's submit hook; this component is presentational + controlled.
 */
export default function UrlInputPanel({
  url,
  attestation,
  isUploading,
  onUrlChange,
  onAttestationChange,
}: UrlInputPanelProps) {
  const { t } = useTranslation(["upload"]);

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="url-input"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]"
        >
          {t("upload:urlInput.label")}
        </label>
        <input
          id="url-input"
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          disabled={isUploading}
          data-test="url-input"
          placeholder={t("upload:urlInput.placeholder")}
          className="w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-4 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      <label className="flex items-start gap-2 text-sm text-[var(--foreground)]">
        <input
          type="checkbox"
          checked={attestation}
          onChange={(e) => onAttestationChange(e.target.checked)}
          disabled={isUploading}
          data-test="url-attestation"
          className="mt-1"
        />
        <span>{t("upload:urlInput.attestation")}</span>
      </label>

      <p
        data-test="url-snapshot-caveat"
        className="text-xs text-[var(--muted-foreground)]"
      >
        {t("upload:urlInput.caveat")}
      </p>
    </div>
  );
}
